class StoreLocatorMap extends HTMLElement {
  static get observedAttributes() {
    return [
      'access-token',
      'style-url',
      'fit-padding',
      'fit-max-zoom',
      'geo-zoom',
      'focus-zoom',
      'initial-center',
      'initial-zoom',
    ]
  }

  constructor() {
    super()
    this.map = null
    this.geocoder = null
    this.stores = []
    this.markers = []
    this.history = []
    this.currentView = { center: null, zoom: null }
    this.selectedId = null
    this.selectedLocation = null // Current selected location for distance calculations
    this._suppressGeocoderClearEvent = false
    this._locateRequestId = 0
    this._locateTimeoutId = null
    this._revealTimeoutId = null
    this._isVisualReady = false
    this._initialDefaultView = null
    this._resetControlButton = null

    this.back = this.back.bind(this)
    this.focusStore = this.focusStore.bind(this)
    this._handleMarkerClick = this._handleMarkerClick.bind(this)
    this._handleResetClick = this._handleResetClick.bind(this)

    this.defaultMarkerIcon = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 0.25C18.4893 0.25 23.75 5.51065 23.75 12C23.75 18.4893 18.4893 23.75 12 23.75C5.51065 23.75 0.25 18.4893 0.25 12C0.25 5.51065 5.51065 0.25 12 0.25Z" fill="white"/>
        <path d="M12 0.25C18.4893 0.25 23.75 5.51065 23.75 12C23.75 18.4893 18.4893 23.75 12 23.75C5.51065 23.75 0.25 18.4893 0.25 12C0.25 5.51065 5.51065 0.25 12 0.25Z" stroke="#0A0000" stroke-width="0.5"/>
        <circle cx="12" cy="12" r="3" fill="#0A0000"/>
      </svg>
    `
  }

  get accessToken() {
    return this.getAttribute('access-token') || ''
  }

  get styleUrl() {
    return this.getAttribute('style-url') || ''
  }

  get searchPlaceholder() {
    return this.getAttribute('search-placeholder') || 'Search by address, city, postcode or store name...'
  }

  get fitPadding() {
    const n = parseFloat(this.getAttribute('fit-padding'))
    return Number.isFinite(n) ? n : 60
  }

  get fitMaxZoom() {
    const n = parseFloat(this.getAttribute('fit-max-zoom'))
    return Number.isFinite(n) ? n : 5
  }

  get geoZoom() {
    const n = parseFloat(this.getAttribute('geo-zoom'))
    return Number.isFinite(n) ? n : 7
  }
  get focusZoom() {
    const n = parseFloat(this.getAttribute('focus-zoom'))
    return Number.isFinite(n) ? n : 13
  }

  get initialCenter() {
    const raw = this.getAttribute('initial-center')
    if (!raw) return null
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed) && parsed.length === 2) return parsed
    } catch (_) { }
    return null
  }

  connectedCallback() {
    this._prepareInitialVisibility()
    this.setStores()
    this._init()
  }

  disconnectedCallback() {
    this._destroy()
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (!this.map || oldVal === newVal) return
    if (name === 'style-url' && newVal) {
      this.map.setStyle(newVal)
    }
  }

  setStores() {
    this.stores = JSON.parse(this.querySelector('[data-stores]')?.textContent || '[]')

    // Sort results based on the order below so that they are sorted correctly in the search suggestion results
    const priorities = {
      Flagship: 0,
      Boutique: 1,
      'Branded Corner': 2,
      'Pop-Up': 3
    }

    this.stores.sort((a, b) => {
      const aRank = priorities[a.boutique_type] ?? 99
      const bRank = priorities[b.boutique_type] ?? 99

      return aRank - bRank
    })
  }

  set view(v) {
    if (!this.map || !v) return
    this._setView({
      center: v.center,
      zoom: v.zoom,
      pushHistory: v.pushHistory !== false,
      animate: v.animate !== false,
      source: 'external',
    })
  }

  get view() {
    return { ...this.currentView }
  }

  // ==================== Geocoder Methods ====================

  /**
   * Initialize the geocoder and add it to a container element
   * @param {HTMLElement} container - The container to add the geocoder to
   */
  initGeocoder(container) {
    if (!container || !this.map) {
      console.error('Cannot init geocoder: missing container or map')
      return
    }

    // Wait for MapboxGeocoder to be available
    const checkAndInit = () => {
      if (typeof MapboxGeocoder !== 'undefined') {
        this._setupGeocoder(container)
      } else {
        setTimeout(checkAndInit, 100)
      }
    }
    checkAndInit()
  }

  _setupGeocoder(container) {
    const getCountryFromFeature = (feature) => {
      const props = feature?.properties || {}
      const ctx = props.context || feature?.context || {}

      // Mapbox responses can provide context either as an object or an array.
      let country = {}
      if (Array.isArray(ctx)) {
        country =
          ctx.find((item) => item?.id?.startsWith('country')) ||
          ctx.find((item) => item?.short_code) ||
          {}
      } else {
        country = ctx.country || {}
      }

      const countryName =
        country.name || country.text || props.country || feature?.text || null
      const rawCode =
        country.country_code ||
        country.short_code ||
        props.country_code ||
        props.short_code ||
        null

      return {
        countryName,
        countryCode: rawCode ? String(rawCode).toLowerCase().replace('country.', '') : null,
      }
    }

    const escapeHtml = (value = '') =>
      String(value).replace(/[&<>"']/g, (char) => {
        const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }
        return map[char] || char
      })

    // Convert stores to GeoJSON features for localGeocoder
    const storeFeatures = this.stores
      .filter((store) => store.coordinates?.length === 2)
      .map((store) => ({
        ...store,
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: store.coordinates,
        },
        properties: {
          title: store.name,
          address: store.queryField?.split(' - ').slice(3).join(' ').trim() || '',
          description: store.queryField?.split(' - ').slice(1, 3).join(' - ') || '',
          storeId: store.id,
          storeType: store.boutique_type,
          storeIcon:
            this.querySelector(
              `template[data-store-icon-html][data-store-type='${store.boutique_type}']`
            )?.innerHTML || this.defaultMarkerIcon,
          queryField: store.queryField || store.name,
        },
      }))

    // Local geocoder function to search stores
    const localGeocoder = (query) => {
      const queryLower = query.toLowerCase()
      const matchingFeatures = []

      for (const feature of storeFeatures) {
        const queryField = feature.properties.queryField?.toLowerCase() || ''
        if (queryField.includes(queryLower)) {
          matchingFeatures.push({
            ...feature,
            place_name: `${feature.properties.title}`,
            center: feature.geometry.coordinates,
            place_type: ['store'],
          })
        }
      }

      return matchingFeatures.slice(0, 5)
    }

    // Create the geocoder
    this.geocoder = new MapboxGeocoder({
      accessToken: this.accessToken,
      mapboxgl: window.mapboxgl,
      localGeocoder: localGeocoder,
      localGeocoderOnly: false,
      placeholder: this.searchPlaceholder,
      marker: false,
      flyTo: false,
      minLength: 2,
      render: (item) => {
        const isStore = item.place_type?.includes('store') && item.properties?.storeId
        if (isStore) {
          const storeName = escapeHtml(item.properties?.title || item.text || '')
          const storeAddress = escapeHtml(item.properties?.address || '')
          const storeIcon = item.properties?.storeIcon || this.defaultMarkerIcon

          return `
            <div class="store-locator-page__suggestion store-locator-page__suggestion--store">
              <span class="store-locator-page__suggestion-icon" aria-hidden="true">${storeIcon}</span>
              <span class="store-locator-page__suggestion-content">
                <span class="store-locator-page__suggestion-title">${storeName}</span>
                ${storeAddress ? `<span class="store-locator-page__suggestion-address">${storeAddress}</span>` : ''}
              </span>
            </div>
          `
        }

        const primary = escapeHtml(item.text || item.place_name?.split(',')[0] || '')
        const secondary = escapeHtml(
          item.place_formatted ||
          item.place_name?.replace(`${item.text || ''}, `, '') ||
          item.place_name ||
          ''
        )

        return `
          <div class="store-locator-page__suggestion">
            <span class="store-locator-page__suggestion-content">
              <span class="store-locator-page__suggestion-title">${primary}</span>
              ${secondary ? `<span class="store-locator-page__suggestion-address">${secondary}</span>` : ''}
            </span>
          </div>
        `
      },
    })

    // Add geocoder to the map (this binds it properly)
    const geocoderElement = this.geocoder.onAdd(this.map)
    container.appendChild(geocoderElement)

    // Handle result selection
    this.geocoder.on('result', (event) => {
      const feature = event.result
      if (!feature) return

      // Check if it's a custom store result
      if (feature.place_type?.includes('store') && feature.properties?.storeId) {
        const storeId = feature.properties.storeId
        const store = this.stores.find((s) => s.id === storeId)

        if (store?.coordinates) {
          this.selectedLocation = {
            lng: store.coordinates[0],
            lat: store.coordinates[1],
          }
          this.focusStore(storeId)
        }
      } else {
        // It's a Mapbox geocoding result
        const coordinates = feature.center || feature.geometry?.coordinates
        if (coordinates) {
          this.selectedLocation = {
            lng: coordinates[0],
            lat: coordinates[1],
          }

          // Fly to the selected location
          this.map.flyTo({
            center: coordinates,
            zoom: this.geoZoom,
            duration: 650,
          })
        }
      }

      const { countryName, countryCode } = feature.place_type?.includes('store')
        ? {
          countryName: this.stores.find((s) => s.id === feature.properties?.storeId)?.country || null,
          countryCode: null,
        }
        : getCountryFromFeature(feature)

      // Emit event for the page component to handle filtering
      this._emit('location-selected', {
        location: this.selectedLocation,
        feature: feature,
        isStore: feature.place_type?.includes('store'),
        storeId: feature.properties?.storeId || null,
        countryName,
        countryCode,
      })
    })

    // Handle clear
    this.geocoder.on('clear', () => {
      this.selectedLocation = null
      this.selectedId = null
      this._applySelection()
      if (this._suppressGeocoderClearEvent) {
        this._suppressGeocoderClearEvent = false
        return
      }
      this._emit('location-cleared', {})
      this.fitToStores({ pushHistory: false })
    })
  }

  /**
   * Clear the geocoder input
   */
  clearGeocoder(opts = {}) {
    if (this.geocoder) {
      this._suppressGeocoderClearEvent = Boolean(opts.silent)
      this.geocoder.clear()
    }
  }

  // ==================== Geolocation Methods ====================

  /**
   * Trigger geolocation to find user's position
   */
  locateUser() {
    this._locateRequestId += 1
    const requestId = this._locateRequestId
    this._emit('location-loading', { requestId })

    clearTimeout(this._locateTimeoutId)
    this._locateTimeoutId = setTimeout(() => {
      if (requestId !== this._locateRequestId) return
      this._completeLocateError({ message: 'Location request timed out' }, requestId)
    }, 8000)

    this._locateWithNavigator(requestId)
  }

  cancelLocateUser() {
    this._locateRequestId += 1
    clearTimeout(this._locateTimeoutId)
    this._locateTimeoutId = null
    this._emit('location-cancelled', {})
  }

  // ==================== Map Methods ====================

  updateActiveStores(activeStores, opts = {}) {
    this.markers.forEach((marker) => {
      const markerElement = marker.__el
      const storeHandle = markerElement.getAttribute('data-store-handle')
      const activeStore = activeStores.find((s) => s.id === storeHandle)
      if (activeStore) {
        markerElement?.classList.remove('hidden')
      } else {
        markerElement?.classList.add('hidden')
      }
    })

    // Fit to active stores with higher zoom allowed for filtered results
    if (opts.fitMap !== false && activeStores.length > 0) {
      const maxZoom = activeStores.length === 1 ? this.focusZoom : 14
      this.fitToStores({ pushHistory: false, maxZoom }, activeStores)
    }
  }

  fitToStores(opts = {}, stores = this.stores) {
    if (!this.map) return
    const bounds = this._calcBoundsFromStores(stores)
    if (!bounds) return
    if (opts.pushHistory !== false) this._pushHistory()
    const isSingleStoreView = bounds[0][0] === bounds[1][0] && bounds[0][1] === bounds[1][1]
    const maxZoom =
      typeof opts.maxZoom === 'number'
        ? opts.maxZoom
        : isSingleStoreView
          ? this.focusZoom
          : this.fitMaxZoom
    this.map.fitBounds(bounds, {
      padding: this.fitPadding,
      maxZoom: maxZoom,
      duration: opts.animate === false ? 0 : 650,
    })
  }

  focusStore(storeOrId, opts = {}) {
    if (!this.map) return
    const store =
      typeof storeOrId === 'object' && storeOrId
        ? storeOrId
        : this.stores.find((s) => String(s.id) === String(storeOrId))
    if (!store || !Array.isArray(store.coordinates)) return
    this.selectedId = String(store.id ?? '')
    this._applySelection()
    const zoom =
      typeof opts.zoom === 'number'
        ? opts.zoom
        : Math.max(this.map.getZoom(), this.focusZoom)
    this._setView({
      center: store.coordinates,
      zoom,
      pushHistory: true,
      animate: true,
      source: 'store',
    })
    this._emit('store-focused', { store })
  }

  back() {
    if (!this.map) return
    const prev = this.history.pop()
    if (!prev) return
    this._setView({
      center: prev.center,
      zoom: prev.zoom,
      pushHistory: false,
      animate: true,
      source: 'back',
    })
  }

  resetToInitialView() {
    this.selectedId = null
    this._applySelection()
    if (this._initialDefaultView) {
      this._setView({
        center: this._initialDefaultView.center,
        zoom: this._initialDefaultView.zoom,
        pushHistory: false,
        animate: true,
        source: 'reset-control',
      })
    } else {
      this.fitToStores({ pushHistory: false })
    }
  }

  restorePreviousView() {
    if (this.history.length > 0) {
      this.back()
    } else {
      this.resetToInitialView()
    }
    this.selectedId = null
    this._applySelection()
  }

  // ==================== Private Methods ====================

  _init() {
    if (this.map) return
    if (!window.mapboxgl || !mapboxgl.Map) {
      throw new Error('mapboxgl not found. Make sure Mapbox GL JS CDN loads before this script.')
    }
    if (!this.accessToken) throw new Error('Missing access-token')
    if (!this.styleUrl) throw new Error('Missing style-url')

    mapboxgl.accessToken = this.accessToken
    const container = this.querySelector('[data-map]') || this

    this.map = new mapboxgl.Map({
      container,
      style: this.styleUrl,
      projection: 'mercator',
      center: [0, 20],
      zoom: 1.2,
    })

    // Add navigation control
    this.map.addControl(
      new mapboxgl.NavigationControl({ showCompass: false }),
      'top-right'
    )
    this._createResetControl()

    this.map.on('moveend', () => {
      const c = this.map.getCenter()
      this.currentView = { center: [c.lng, c.lat], zoom: this.map.getZoom() }
      this._emit('view-changed', { ...this.currentView, source: 'user' })
      this._updateResetControlVisibility()
    })

    this.map.on('load', () => {
      this.map.resize()
      if (this.stores.length) {
        this._renderMarkers()
        if (!this.initialCenter) {
          this.fitToStores({ pushHistory: false })
        }
      }
      this._emit('ready', {})
      this._revealWhenStable()
      this.map.once('idle', () => {
        if (!this._initialDefaultView) {
          const c = this.map.getCenter()
          this._initialDefaultView = {
            center: [c.lng, c.lat],
            zoom: this.map.getZoom(),
          }
        }
        this._updateResetControlVisibility()
      })
    })
  }

  _destroy() {
    try {
      this._clearMarkers()
      if (this.map) this.map.remove()
    } finally {
      clearTimeout(this._locateTimeoutId)
      clearTimeout(this._revealTimeoutId)
      this.map = null
      this.geocoder = null
      this._locateTimeoutId = null
      this._revealTimeoutId = null
      if (this._resetControlButton) {
        this._resetControlButton.removeEventListener('click', this._handleResetClick)
        this._resetControlButton.remove()
      }
      this._resetControlButton = null
      this._initialDefaultView = null
    }
  }

  _createResetControl() {
    if (this._resetControlButton) return
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = 'mapboxgl-ctrl-icon store-locator-map__reset-control'
    btn.setAttribute('aria-label', 'Reset map view')
    btn.setAttribute('title', 'Reset map view')
    btn.addEventListener('click', this._handleResetClick)

    const navGroup = this.querySelector('.mapboxgl-ctrl-top-right .mapboxgl-ctrl-group')
    if (navGroup) {
      navGroup.appendChild(btn)
    } else {
      // Fallback, should rarely happen.
      this.appendChild(btn)
    }

    this._resetControlButton = btn
    this._updateResetControlVisibility()
  }

  _handleResetClick() {
    this.resetToInitialView()
  }

  _updateResetControlVisibility() {
    if (!this._resetControlButton || !this.map || !this._initialDefaultView) return

    const c = this.map.getCenter()
    const z = this.map.getZoom()
    const [defaultLng, defaultLat] = this._initialDefaultView.center
    const defaultZoom = this._initialDefaultView.zoom

    const hasMoved =
      Math.abs(c.lng - defaultLng) > 0.0005 ||
      Math.abs(c.lat - defaultLat) > 0.0005 ||
      Math.abs(z - defaultZoom) > 0.05

    this._resetControlButton.classList.toggle('is-visible', hasMoved)
  }

  _prepareInitialVisibility() {
    if (this._isVisualReady) return
    this.style.opacity = '0'
    this.style.transition = this.style.transition || 'opacity 0.25s ease'
    this.style.willChange = 'opacity'
  }

  _revealWhenStable() {
    if (this._isVisualReady || !this.map) return

    const reveal = () => {
      if (this._isVisualReady) return
      this._isVisualReady = true
      this.style.opacity = '1'
      this.removeAttribute('aria-busy')
      this.setAttribute('data-map-ready', '')
      clearTimeout(this._revealTimeoutId)
      this._revealTimeoutId = null
    }

    this.setAttribute('aria-busy', 'true')
    this.map.once('idle', reveal)
    // Fallback in case idle takes too long on slow connections/devices
    this._revealTimeoutId = setTimeout(reveal, 700)
  }

  _locateWithNavigator(requestId) {
    if (!navigator.geolocation) {
      this._completeLocateError({ message: 'Geolocation not supported' }, requestId)
      return
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const center = [pos.coords.longitude, pos.coords.latitude]
        this._setView({
          center,
          zoom: this.geoZoom,
          pushHistory: true,
          animate: true,
          source: 'geolocation-navigator',
        })
        this._completeLocateSuccess(center, 'navigator', requestId)
      },
      (err) => {
        this._completeLocateError({ code: err.code, message: err.message }, requestId)
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 }
    )
  }

  _completeLocateSuccess(center, source = 'navigator', requestId = this._locateRequestId) {
    if (requestId !== this._locateRequestId) return
    clearTimeout(this._locateTimeoutId)
    this._locateTimeoutId = null
    this.selectedLocation = { lng: center[0], lat: center[1] }
    this._resolveCountryFromCoordinates(center).then(({ countryName, countryCode }) => {
      if (requestId !== this._locateRequestId) return
      this._emit('user-location', {
        center,
        location: this.selectedLocation,
        source,
        countryName,
        countryCode,
      })
    })
  }

  _completeLocateError(detail, requestId = this._locateRequestId) {
    if (requestId !== this._locateRequestId) return
    clearTimeout(this._locateTimeoutId)
    this._locateTimeoutId = null
    this._emit('location-error', detail)
  }

  async _resolveCountryFromCoordinates(center) {
    if (!Array.isArray(center) || center.length !== 2 || !this.accessToken) {
      return { countryName: null, countryCode: null }
    }

    const [lng, lat] = center
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?types=country&limit=1&access_token=${encodeURIComponent(this.accessToken)}`

    try {
      const response = await fetch(url, { method: 'GET' })
      if (!response.ok) return { countryName: null, countryCode: null }
      const data = await response.json()
      const feature = data?.features?.[0]
      if (!feature) return { countryName: null, countryCode: null }

      return {
        countryName: feature.text || feature.place_name || null,
        countryCode: feature.properties?.short_code
          ? String(feature.properties.short_code).toLowerCase()
          : null,
      }
    } catch (_) {
      return { countryName: null, countryCode: null }
    }
  }

  _renderMarkers() {
    this._clearMarkers()
    for (const store of this.stores) {
      if (!store || !Array.isArray(store.coordinates) || store.coordinates.length !== 2) continue

      const el = document.createElement('div')
      el.className = 'sl-marker'
      el.setAttribute('data-store-handle', store.id)
      el.setAttribute('data-store-type', store.boutique_type)
      el.setAttribute('data-store-locator-pin', '')
      el.innerHTML =
        this.querySelector(
          `template[data-store-icon-html][data-store-type='${store.boutique_type}']`
        )?.innerHTML || this.defaultMarkerIcon
      el.addEventListener('click', this._handleMarkerClick)

      const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
        .setLngLat(store.coordinates)
        .addTo(this.map)
      marker.__id = String(store.id ?? '')
      marker.__el = el
      this.markers.push(marker)
    }
    this._applySelection()
  }

  _clearMarkers() {
    for (const m of this.markers) {
      try {
        m.remove()
      } catch (_) { }
    }
    this.markers = []
  }

  _applySelection() {
    for (const m of this.markers) {
      if (!m.__el) continue
      const isSelected = this.selectedId && m.__id === this.selectedId
      m.__el.classList.toggle('is-selected', Boolean(isSelected))
    }
  }

  _pushHistory() {
    if (!this.map) return
    const c = this.map.getCenter()
    this.history.push({ center: [c.lng, c.lat], zoom: this.map.getZoom() })
  }

  _setView({ center, zoom, pushHistory, animate, source }) {
    if (!this.map) return
    if (pushHistory) this._pushHistory()
    const current = this.map.getCenter()
    const nextCenter = center || [current.lng, current.lat]
    const nextZoom = typeof zoom === 'number' ? zoom : this.map.getZoom()
    if (animate) {
      this.map.easeTo({ center: nextCenter, zoom: nextZoom, duration: 650 })
    } else {
      this.map.jumpTo({ center: nextCenter, zoom: nextZoom })
    }
    this.currentView = { center: nextCenter, zoom: nextZoom }
    this._emit('view-changed', {
      ...this.currentView,
      source: source || 'unknown',
    })
  }

  _calcBoundsFromStores(stores) {
    let minLng = Infinity,
      minLat = Infinity,
      maxLng = -Infinity,
      maxLat = -Infinity
    let any = false
    for (const s of stores) {
      const c = s && s.coordinates
      if (!Array.isArray(c) || c.length !== 2) continue
      const lng = Number(c[0])
      const lat = Number(c[1])
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue
      any = true
      minLng = Math.min(minLng, lng)
      minLat = Math.min(minLat, lat)
      maxLng = Math.max(maxLng, lng)
      maxLat = Math.max(maxLat, lat)
    }
    if (!any) return null
    return [
      [minLng, minLat],
      [maxLng, maxLat],
    ]
  }

  _emit(name, detail) {
    this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }))
  }

  _handleMarkerClick(e) {
    const storeHandle = e.currentTarget.getAttribute('data-store-handle')
    const store = this.stores.find((s) => String(s.id) === storeHandle)

    document
      .querySelector(`[data-store-drawer-toggle][data-store-handle='${storeHandle}']`)
      ?.dispatchEvent(
        new CustomEvent('openDrawer', {
          bubbles: true,
          composed: true,
          detail: { key: `store--${storeHandle}` },
        })
      )

    if (store) {
      this.focusStore(store.id)
      this.selectedId = String(store.id ?? '')
      this._applySelection()
      this._emit('store-selected', { store })
    }
  }
}

customElements.define('store-locator-map', StoreLocatorMap)
